

import React, { useState, useEffect } from 'react';
import { Box, Container, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PhlebotomistOrderList from '../Orderlist';
import PhlebotomistOrderDetails from "./Phlebotomistorderdetails";
import SampleCollectionForm from "./SampleCollection";
import LabTransfer from "./LabTransfer";
import FailureReasonScreen from "./Failurereasonscreen";
import CompletionScreen from "./Completionscreen";
import PaymentCollection from '../Paymentcollection';

const PhlebotomistDashboard = () => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState('orderList');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── auth headers ──────────────────────────────────────────────────────────
  const getAuthHeaders = () => {
    const userId = sessionStorage.getItem('userId');
    const token  = sessionStorage.getItem('token');
    const headers = { 'X-User-Id': userId };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  // ── localStorage progression tracker ─────────────────────────────────────
  const getOrderProgression = (orderId) => {
    const saved = localStorage.getItem(`phlebotomist_order_${orderId}`);
    return saved ? JSON.parse(saved) : {
      orderDetailsViewed: false,
      assignmentAccepted: false,
      samplesCollected:   false,
      otpVerified:        false,
      paymentCollected:   false,
      labTransferred:     false,
      completed:          false
    };
  };

  const updateOrderProgression = (orderId, updates) => {
    const current = getOrderProgression(orderId);
    const updated = { ...current, ...updates };
    localStorage.setItem(`phlebotomist_order_${orderId}`, JSON.stringify(updated));
    return updated;
  };

  const clearOrderProgression = (orderId) => {
    localStorage.removeItem(`phlebotomist_order_${orderId}`);
  };

  // ── FIXED: payment status derived from payment.method ────────────────────
  // online  → paid via gateway, phlebotomist does NOT collect
  // offline → phlebotomist must collect; paymentCollectedAt tells us if already done
  const getPaymentStatus = (order) => {
    const method = (order.paymentMethod || '').toLowerCase();

    if (method === 'online') {
      return { needsPayment: false, isPaid: true };
    }

    // offline / cod / cash / empty → check if already collected
    const alreadyCollected = Boolean(order.paymentCollectedAt) || order.isPaid === true;
    return {
      needsPayment: !alreadyCollected,
      isPaid: alreadyCollected
    };
  };

  // ── load agent on mount ──────────────────────────────────────────────────
  useEffect(() => {
    const loadAgentData = async () => {
      const userId = sessionStorage.getItem('userId');
      let agent_id  = sessionStorage.getItem('agent_id');

      if (!agent_id) {
        const storedUser = sessionStorage.getItem('user');
        if (storedUser) {
          try { agent_id = JSON.parse(storedUser).agent_id; } catch (_) {}
        }
      }

      if (!agent_id) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API}/deliveryagent/agent-by-user/${userId}`,
            { headers: getAuthHeaders() }
          );
          if (response.data.success) {
            agent_id = response.data.data.agentId;
            sessionStorage.setItem('agent_id', agent_id);
            setAgentData(response.data.data);
            return;
          }
        } catch (err) {
          console.error('Error fetching agent by userId:', err);
          setError('Failed to load agent data');
          return;
        }
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/deliveryagent/delivery-agents/${agent_id}`,
          { headers: getAuthHeaders() }
        );
        setAgentData(response.data);
      } catch (err) {
        console.error('Error fetching agent data:', err);
        setError('Failed to load agent data');
      }
    };
    loadAgentData();
  }, [navigate]);

  // ── fetch orders ──────────────────────────────────────────────────────────
  // ✅ FIXED: Only fetch BOOKING orders for phlebotomist
  const fetchOrders = async (filter = null) => {
    setLoading(true);
    setError(null);
    try {
      const userId = sessionStorage.getItem('userId');
      if (!userId) throw new Error('User ID not found');

      console.log('Fetching BOOKING orders for phlebotomist, userId:', userId, 'filter:', filter);

      const params = { 
        user_id: userId, 
        orderType: 'booking' // ✅ CRITICAL: Only fetch booking orders
      };
      
      if (filter) params.filter = filter;

      const response = await axios.get(
        `${process.env.REACT_APP_API}/deliveryagent/my-orders`,
        { params, headers: getAuthHeaders() }
      );

      console.log('Booking orders fetched:', response.data);

      // ✅ CRITICAL: Filter to ensure only booking orders
      const bookingOrders = (response.data.data || []).filter(
        order => order.orderType === 'booking'
      );

      setOrders(bookingOrders);
      
      if (response.data.agentInfo) {
        setAgentData(prev => ({ ...prev, ...response.data.agentInfo }));
      }
    } catch (err) {
      console.error('Error fetching booking orders:', err);
      setError(err.response?.data?.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  /**
   * ✅ BOOKING ORDER WORKFLOW ONLY (PHLEBOTOMIST)
   * orderDetails → sampleCollection → paymentCollection (OTP + Payment) → labTransfer → completion
   */
  const determineNextScreen = (order, forceScreen = null) => {
    console.log('=== DETERMINING NEXT SCREEN (PHLEBOTOMIST) ===');
    console.log('Order ID:', order.id, '| Status:', order.orderStatus, '| Assignment:', order.assignmentStatus);
  
    // ✅ CRITICAL: Ensure this is a booking order
    if (order.orderType !== 'booking') {
      console.error('❌ NON-BOOKING ORDER DETECTED IN PHLEBOTOMIST DASHBOARD');
      setError('This dashboard is for diagnostic test bookings only');
      return 'orderList';
    }

    if (forceScreen) {
      console.log('→ forced:', forceScreen);
      return forceScreen;
    }
  
    const progression  = getOrderProgression(order.id);
    const paymentStatus = getPaymentStatus(order);
  
    console.log('Payment:', paymentStatus, '| Progression:', progression);
  
    // Terminal states
    if (order.orderStatus === 'completed' || progression.completed) {
      return 'completion';
    }
  
    // Lab transfer done - show completion
    if (
      order.orderStatus === 'test-in-progress' ||
      order.orderStatus === 'test-in-progress' ||
      order.orderStatus === 'lab-received' ||
      order.orderStatus === 'report-ready' ||
      progression.labTransferred
    ) {
      console.log('→ lab transfer done, showing completion');
      return 'completion';
    }
  
    // Samples collected - check OTP & payment before lab transfer
    const isSampleCollected =
      order.orderStatus === 'sample-collected' ||
      progression.samplesCollected;
  
    if (isSampleCollected) {
      console.log('→ samples collected');
  
      const otpDone = progression.otpVerified;
      const paymentDone = !paymentStatus.needsPayment || progression.paymentCollected;
  
      console.log(`  OTP: ${otpDone}, Payment: ${paymentDone}`);
  
      // If OTP or payment missing, show payment collection
      if (!otpDone || !paymentDone) {
        console.log('→ needs OTP / payment');
        return 'paymentCollection';
      }
  
      // Both done - ready for lab transfer
      console.log('→ ready for labTransfer');
      return 'labTransfer';
    }
  
    // Assignment accepted - show sample collection
    if (
      (order.assignmentStatus === 'accepted' || progression.assignmentAccepted) &&
      progression.orderDetailsViewed
    ) {
      console.log('→ showing sampleCollection');
      return 'sampleCollection';
    }
  
    // Default - start at order details
    console.log('→ starting from orderDetails');
    return 'orderDetails';
  };

  // ── order selection ───────────────────────────────────────────────────────
  const handleOrderSelect = (order) => {
    console.log('=== BOOKING ORDER SELECTED ===', order.id);
    
    // ✅ CRITICAL: Validate order type
    if (order.orderType !== 'booking') {
      setError('Invalid order type. This dashboard is for diagnostic test bookings only.');
      return;
    }
    
    setSelectedOrder(order);
    
    // Determine correct screen based on order state
    const nextScreen = determineNextScreen(order);
    console.log(`→ Navigating to: ${nextScreen}`);
    
    setCurrentScreen(nextScreen);
  };

  const handleBackToOrders = () => {
    setCurrentScreen('orderList');
    setSelectedOrder(null);
    fetchOrders();
  };

  // ── refresh order from backend, optionally update progression, then route ──
  const handleOrderUpdate = async (nextScreen = null, progressionUpdates = null) => {
    try {
      const userId = sessionStorage.getItem('userId');
      console.log('=== UPDATING BOOKING ORDER ===', selectedOrder?.id, '| next:', nextScreen);

      const response = await axios.get(
        `${process.env.REACT_APP_API}/deliveryagent/my-orders`,
        {
          params: { 
            user_id: userId, 
            orderType: 'booking' // ✅ Only fetch booking orders
          },
          headers: getAuthHeaders()
        }
      );

      const updatedOrder = response.data.data.find(o => o.id === selectedOrder.id);

      if (updatedOrder) {
        console.log('Refreshed booking order:', {
          isPaid: updatedOrder.isPaid,
          orderStatus: updatedOrder.orderStatus,
          assignmentStatus: updatedOrder.assignmentStatus,
          paymentMethod: updatedOrder.paymentMethod
        });

        setSelectedOrder(updatedOrder);

        if (progressionUpdates) {
          updateOrderProgression(selectedOrder.id, progressionUpdates);
        }

        const screenToShow = determineNextScreen(updatedOrder, nextScreen);
        console.log('→ navigating to:', screenToShow);
        setCurrentScreen(screenToShow);
      }
    } catch (err) {
      console.error('Error updating booking order:', err);
      setError('Failed to refresh order data');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER SCREENS
  // ─────────────────────────────────────────────────────────────────────────
  const renderScreen = () => {
    switch (currentScreen) {

      /* ── order list ───────────────────────────────────────────────────── */
      case 'orderList':
        return (
          <PhlebotomistOrderList
            orders={orders}
            loading={loading}
            onOrderSelect={handleOrderSelect}
            onRefresh={fetchOrders}
            agentData={agentData}
          />
        );

      /* ── order details (accept / reject / start collection) ───────────── */
      case 'orderDetails':
        return (
          <PhlebotomistOrderDetails
            order={selectedOrder}
            onBack={handleBackToOrders}
            onOrderUpdate={async (nextScreenOverride) => {
              updateOrderProgression(selectedOrder.id, { orderDetailsViewed: true });
              await handleOrderUpdate(nextScreenOverride || null, { orderDetailsViewed: true });
            }}
            onReportFailure={() => setCurrentScreen('failureReason')}
          />
        );

      /* ── sample collection form ───────────────────────────────────────── */
      case 'sampleCollection':
        return (
          <SampleCollectionForm
            order={selectedOrder}
            onBack={() => setCurrentScreen('orderDetails')}
            onSampleCollected={async (collectionData) => {
              console.log('✅ Samples collected:', collectionData);
              setSelectedOrder(prev => ({
                ...prev,
                orderStatus: 'sample-collected',
                sampleCollections: collectionData
              }));
              updateOrderProgression(selectedOrder.id, { samplesCollected: true });
              await handleOrderUpdate(null, { samplesCollected: true });
            }}
            onReportFailure={() => setCurrentScreen('failureReason')}
          />
        );

      /* ── OTP + payment collection ─────────────────────────────────────── */
      case 'paymentCollection':
        return (
          <PaymentCollection
            order={selectedOrder}
            onBack={() => setCurrentScreen('sampleCollection')}
            onPaymentConfirmed={async () => {
              console.log('✅ OTP + Payment confirmed for booking');
              await handleOrderUpdate(null, { otpVerified: true, paymentCollected: true });
            }}
          />
        );

      /* ── lab transfer ─────────────────────────────────────────────────── */
      case 'labTransfer':
        return (
          
          <LabTransfer
            samples={selectedOrder?.sampleCollections || []}
            order={selectedOrder}
            onBack={() => {
              const progression = getOrderProgression(selectedOrder.id);
              const paymentStatus = getPaymentStatus(selectedOrder);
              
              if (!progression.otpVerified || 
                  (paymentStatus.needsPayment && !progression.paymentCollected)) {
                setCurrentScreen('paymentCollection');
              } else {
                setCurrentScreen('sampleCollection');
              }
            }}
            onTransferComplete={(transferData) => {
              console.log('✅ Lab transfer confirmed:', transferData);
              
              setSelectedOrder(prev => ({
                ...prev,
                orderStatus: 'test-in-progress',
                labTransfers: [transferData]
              }));
              
              updateOrderProgression(selectedOrder.id, { labTransferred: true });
              setCurrentScreen('completion');
            }}
          />
        );

      /* ── failure reason ───────────────────────────────────────────────── */
      case 'failureReason':
        return (
          <FailureReasonScreen
            order={selectedOrder}
            onBack={() => setCurrentScreen('orderDetails')}
            onFailureSubmitted={(failureData) => {
              console.log('Failure reported:', failureData);
              clearOrderProgression(selectedOrder.id);
              handleBackToOrders();
            }}
          />
        );

      /* ── completion ───────────────────────────────────────────────────── */
      case 'completion':
        return (
          <CompletionScreen
            order={selectedOrder}
            onBackToOrders={() => {
              if (selectedOrder?.id) clearOrderProgression(selectedOrder.id);
              handleBackToOrders();
            }}
          />
        );

      /* ── fallback ─────────────────────────────────────────────────────── */
      default:
        return <Alert severity="error">Unknown screen: {currentScreen}</Alert>;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      {error && (
        <Container maxWidth="lg" sx={{ pt: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        </Container>
      )}
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {renderScreen()}
      </Container>
    </Box>
  );
};

export default PhlebotomistDashboard;